"use client";

/**
 * HeroUrlForm — inline "paste a URL and go" entry point used by the
 * conversion-focused hero / final-CTA sections.
 *
 * Why it exists: every other CTA on the marketing site used to link straight
 * to /audit/new, which bounces guests to the login wall before they see any
 * value. This form removes that wall for logged-out visitors:
 *
 *   - Guests: start a real desktop-only audit immediately (no
 *     login), then watch it run on /try/[jobId]. The result is gated behind a
 *     "sign in to unlock" step that claims the audit onto their account.
 *   - Signed-in users: go to the full /audit/new configurator (their plan,
 *     all viewports/options), prefilled with the URL.
 *
 * The "site card" under the input (shared with /audit/new — see
 * components/site-preview.tsx) shows EVIDENCE of what's at the address:
 * the page's real title, an empty-shell warning (airdnb.com), a cross-host
 * redirect, or "did you mean airbnb.com?". Warn-level verdicts arm a
 * one-click "are you sure?" step on submit; nothing is ever hard-blocked.
 *
 * Two tones: "light" on the paper hero, "dark" on the final-CTA panel.
 */

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { AUDIT_URL_INPUT_ID } from "../constants";
import {
  isWarnPreview,
  SiteFavicon,
  SitePreviewMessage,
  useSitePreflight,
} from "../components/site-preview";
import { useAccountState } from "../components/use-account-state";
import { ApiError } from "../lib/api";
import {
  normalizeUrl,
  startGuestAuditFlow,
  validateAuditUrl,
} from "../lib/guest-audit";
import { isOurOwnDomain, SELF_AUDIT_MESSAGE } from "../lib/self-domain";
import { track } from "../lib/track";

interface HeroUrlFormProps {
  tone?: "light" | "dark";
  buttonLabel?: string;
  placeholder?: string;
  /** Which instance this is ("hero", "sticky", "final-cta", "pricing") —
   *  carried on the Guest Run Started event so the funnel shows which
   *  surface actually converts. */
  surface?: string;
}

export function HeroUrlForm({
  tone = "light",
  buttonLabel = "Check my site free",
  placeholder = "yoursite.com",
  surface = "hero",
}: HeroUrlFormProps) {
  const router = useRouter();
  const account = useAccountState();
  const [url, setUrl] = useState("");
  const engagedRef = useRef(false);
  const landingViewedRef = useRef(false);
  useEffect(() => {
    if (surface !== "hero" || account.status !== "anonymous" || landingViewedRef.current) return;
    landingViewedRef.current = true;
    track("Guest Landing Viewed");
  }, [surface, account.status]);
  function trackEngagement() {
    if (engagedRef.current || account.status === "authenticated") return;
    engagedRef.current = true;
    track("Guest Form Engaged", { surface });
  }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A one-click correction offered next to the error ("easy mails.cc" →
  // "easymails.cc"). Fills the input; deliberately never auto-runs.
  const [suggestion, setSuggestion] = useState<string | null>(null);
  // Armed by the first submit while a warn-level preview is showing: the
  // card flips to "are you sure?" and the button to "Audit anyway", and only
  // the SECOND submit proceeds. One extra click, only when suspicious —
  // never a block. Reset the moment the input names a different host.
  const [confirmArmed, setConfirmArmed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // When the confirm was armed — clicks within the cooldown are swallowed so
  // the second half of a double-click can't self-confirm a question the user
  // never saw.
  const armedAt = useRef(0);
  // Submit arrived before the typing preview had a verdict: we hold the
  // click and resolve the SAME preflight (shared request), button reads
  // "Checking the address…". Capped — a slow check fails open, never blocks.
  const [checking, setChecking] = useState(false);
  const disarmConfirm = useCallback(() => setConfirmArmed(false), []);
  const { preview, ensure } = useSitePreflight(url, surface, disarmConfirm);

  // Demo CTAs return to /#audit-url. Once the homepage has mounted, put the
  // URL field in the middle of the viewport and focus it so the visitor can
  // type immediately. This is a scroll/focus handoff, not browser page zoom.
  useEffect(() => {
    if (
      surface !== "hero" ||
      window.location.hash !== `#${AUDIT_URL_INPUT_ID}`
    ) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      input.focus({ preventScroll: true });
      input.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "center",
        inline: "nearest",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [surface]);

  async function handlePaste() {
    if (submitting || checking) return;
    try {
      const pasted = await navigator.clipboard.readText();
      const next = pasted.trim();
      if (!next) {
        setError("Your clipboard does not contain a URL yet.");
        inputRef.current?.focus();
        return;
      }
      setUrl(next);
      setError(null);
      setSuggestion(null);
      setConfirmArmed(false);
      inputRef.current?.focus();
      track("Guest URL Pasted", { surface });
    } catch {
      setError("Tap the URL field, then paste your website address.");
      inputRef.current?.focus();
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || checking) return;
    const trimmed = url.trim();
    if (!trimmed) {
      track("Guest URL Rejected", { surface, reason: "empty" });
      setError("Enter a URL to audit.");
      return;
    }
    setError(null);
    setSuggestion(null);
    if (isOurOwnDomain(trimmed)) {
      track("Guest URL Rejected", { surface, reason: "self_domain" });
      setError(SELF_AUDIT_MESSAGE);
      return;
    }
    // Structural sanity BEFORE spending a run (server re-validates). Catches
    // "easy mails.cc" instantly with a one-click correction instead of a
    // created-then-failed job.
    const valid = validateAuditUrl(trimmed);
    if (!valid.ok) {
      track("Guest URL Rejected", { surface, reason: "invalid" });
      setError(valid.message);
      setSuggestion(valid.suggestion ?? null);
      return;
    }

    // Double-click guard: an armed confirm ignores clicks for a beat, so
    // "click-click" can't blow through a question that hadn't even painted.
    if (confirmArmed && Date.now() - armedAt.current < 600) return;

    // "Are you sure?" — one interception when the verdict for THE HOST BEING
    // SUBMITTED is warn-level. If the typing preview hasn't resolved yet
    // (fast paste-and-click), hold the click and resolve it now — this is
    // what stops a quick click from outrunning the warning. Still fail-open:
    // a timed-out or failed check proceeds like today.
    if (!confirmArmed) {
      let verdict =
        preview.status === "ready" && preview.hostname === valid.hostname
          ? preview
          : null;
      if (!verdict) {
        setChecking(true);
        const resolved = await ensure(trimmed);
        setChecking(false);
        if (
          resolved.status === "ready" &&
          resolved.hostname === valid.hostname
        ) {
          verdict = resolved;
        }
      }
      if (verdict && isWarnPreview(verdict)) {
        setConfirmArmed(true);
        armedAt.current = Date.now();
        track("Guest URL Confirm Asked", { surface, verdict: verdict.verdict });
        return;
      }
    }

    // Signed-in users get the full configurator, prefilled with the URL.
    if (account.status === "authenticated") {
      router.push(`/audit/new?url=${encodeURIComponent(normalizeUrl(trimmed))}`);
      return;
    }

    // Guests (and the brief "loading" window) start a guest audit in place.
    track("Guest Run Attempted", { surface });
    setSubmitting(true);
    try {
      const jobId = await startGuestAuditFlow(trimmed);
      // Fired only on a successful start — this is the funnel's "Run"
      // step, the number the whole homepage exists to move.
      track("Guest Run Started", { surface });
      router.push(`/try/${jobId}`);
    } catch (err) {
      track("Guest Run Failed", {
        surface,
        status: err instanceof ApiError ? err.status : "unknown",
      });
      setSubmitting(false);
      if (err instanceof ApiError && err.status === 402) {
        setError(
          "You've used your free guest audits for now — sign in to keep going."
        );
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Something went wrong starting your audit. Try again."
        );
      }
    }
  }

  return (
    <div className={`lp-hero-form lp-hero-form-${tone}`}>
      <form className="lp-hero-form-bar" onSubmit={handleSubmit}>
        <div className="lp-hero-form-input-wrap">
          <input
            ref={inputRef}
            id={surface === "hero" ? AUDIT_URL_INPUT_ID : undefined}
            className="lp-hero-form-input"
            type="text"
            inputMode="url"
            aria-label="Website URL to audit"
            placeholder={placeholder}
            value={url}
            onFocus={trackEngagement}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError(null);
              if (suggestion) setSuggestion(null);
              if (confirmArmed) setConfirmArmed(false);
            }}
            autoComplete="url"
            autoCapitalize="none"
            spellCheck={false}
            disabled={submitting || checking}
          />
          <button
            className="lp-hero-form-paste"
            type="button"
            onClick={handlePaste}
            disabled={submitting || checking}
          >
            Paste
          </button>
        </div>
        <button
          className="lp-btn lp-btn-primary lp-hero-form-btn"
          type="submit"
          disabled={submitting || checking}
        >
          {submitting
            ? "Starting…"
            : checking
              ? "Checking the address…"
              : confirmArmed
                ? "Audit anyway"
                : account.status === "authenticated" ? "Start an audit" : buttonLabel}
        </button>
      </form>
      {error && (
        <p className="lp-hero-form-error" role="alert">
          {error}
          {suggestion && (
            <>
              {" "}
              <button
                type="button"
                className="lp-hero-form-suggest"
                onClick={() => {
                  setUrl(suggestion);
                  setError(null);
                  setSuggestion(null);
                }}
              >
                Did you mean {suggestion}?
              </button>
            </>
          )}
        </p>
      )}
      {/* Site-card preview: identity evidence (favicon, hostname, real page
          title), a warning with a one-click fix, or an "are you sure?" ask.
          Hidden while an error owns the line below the bar. */}
      {!error && preview.status === "ready" && (
        <p
          className={`lp-hero-form-preview${
            isWarnPreview(preview) || preview.verdict === "noexist"
              ? " is-warn"
              : ""
          }`}
          aria-live="polite"
        >
          <SiteFavicon hostname={preview.hostname} />
          <span>
            <SitePreviewMessage
              preview={preview}
              confirmArmed={confirmArmed}
              onSwap={(host) => {
                setUrl(host);
                setConfirmArmed(false);
                track("Guest URL Suggestion Taken", { surface });
              }}
            />
          </span>
        </p>
      )}
    </div>
  );
}
